// OWNER: platform module. Lightweight screen-view tracker (AN-1 "feature usage by screen"): posts route PATTERNS
// only (never ids/PII) to POST /api/w/:wsId/platform/analytics/screen-views. Mounted by app/layout/AppShell.tsx.
//  - de-duplicated: the same screen twice in a row is counted once (re-renders, query-string changes)
//  - batched: queued and sent every few seconds (max 20 per request), and when the tab is hidden
//  - fire-and-forget: errors are ignored, no sign-in redirect, no read-only banner
//  - respects Do Not Track / Global Privacy Control (nothing is queued)
//  - off in unit tests unless `trackerConfig.enabled` is set, so other features' tests see no extra requests
import { useEffect } from 'react';
import { useLocation, useMatches } from 'react-router';
import { api, wsPath } from '../../../lib/api';
import { screenSlugFromPath, trackingDisabledByBrowser } from './screenSlug';

export const trackerConfig = {
  enabled: import.meta.env.MODE !== 'test',
  flushMs: 5_000,
  maxBatch: 20,
};

interface Queue {
  wsId: string;
  screens: string[];
}

let queue: Queue | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let lastKey: string | null = null;

/** Sends whatever is queued (exported for tests). */
export function flushScreenViews(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const q = queue;
  queue = null;
  if (!q || q.screens.length === 0) return;
  for (let i = 0; i < q.screens.length; i += trackerConfig.maxBatch) {
    const screens = q.screens.slice(i, i + trackerConfig.maxBatch);
    void api
      .post(wsPath(q.wsId, 'platform', 'analytics', 'screen-views'), { screens }, { redirectOn401: false })
      .catch(() => undefined);
  }
}

/** Queues one screen view (exported for tests). */
export function recordScreenView(wsId: string, screen: string): void {
  const key = `${wsId}|${screen}`;
  if (key === lastKey) return;
  lastKey = key;
  if (queue && queue.wsId !== wsId) flushScreenViews();
  queue ??= { wsId, screens: [] };
  queue.screens.push(screen);
  if (queue.screens.length >= trackerConfig.maxBatch) {
    flushScreenViews();
    return;
  }
  if (!timer) timer = setTimeout(flushScreenViews, trackerConfig.flushMs);
}

/** Test helper: forget queued views and the de-duplication state. */
export function resetScreenViewTracker(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  queue = null;
  lastKey = null;
}

export function PageViewTracker() {
  const { pathname } = useLocation();
  // The deepest match carries every route param (a layout's useParams only sees its own and its parents').
  const matches = useMatches();
  const params = (matches[matches.length - 1]?.params ?? {}) as Record<string, string | undefined>;
  const wsId = params.wsId;

  useEffect(() => {
    if (!trackerConfig.enabled || !wsId || trackingDisabledByBrowser()) return;
    const slug = screenSlugFromPath(pathname, params);
    if (slug) recordScreenView(wsId, slug);
    // params are derived from pathname; depending on pathname is enough
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, wsId]);

  useEffect(() => {
    if (!trackerConfig.enabled) return;
    const onHide = () => {
      if (document.visibilityState === 'hidden') flushScreenViews();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flushScreenViews);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flushScreenViews);
    };
  }, []);

  return null;
}
